import { Maybe } from "@flockcover/flock-svc-core-types"
import { RouteComponentProps } from "@reach/router"
import { DocumentTitleOverride, hooks } from "@shared/primitive-components"
import { emptyCollection } from "@shared/types"
import { useState } from "react"
import { ListWrapper } from "@shared/application-components"
import { useLDFlags } from "@shared/integration/launchdarkly"
import { useAuth } from "../../../contexts"
import { useEnsureOwnerId } from "../../auth/api/useEnsureOwnerId"
import { useViewer } from "../../auth/ui/ViewerProvider"
import { Policy, Status, useSearchPolicies } from "../api/useSearchPolicies"
import { useBrokerConnectivity } from "../api/useBrokerConnectivity"
import { PoliciesTable } from "./policiesComponents/PoliciesTable"
import { OverallConnectivity } from "./policiesComponents/OverallConnectivity"
import { IFrame } from "../../../integration/iframe"

export const POLICIES_DTI = {
  CONNECTIVITY_BAR_DTI: `policies_connectivity_bar`,
}

export const Policies: React.FC<RouteComponentProps> = () => {
  useEnsureOwnerId()

  const { showNewPoliciesPage } = useLDFlags()

  if (showNewPoliciesPage) return <PolicyPage />

  return <LegacyPolicyPage />
}

const PolicyPage = () => (
  <>
    <DocumentTitleOverride>Flock Cover | Policies</DocumentTitleOverride>
    <IFrame path="policies" />
  </>
)

const LegacyPolicyPage = () => {
  const [search, setSearch, debouncedSearch] = hooks.useDebouncedState<string>(``)
  const [status, setStatus] = useState<Maybe<Status>>(`Active`)
  const [limit, setLimit] = useState<number>(10)
  const { viewer } = useViewer()
  const auth = useAuth()
  const userType = auth.getAuthType()
  const [resConnectivity] = useBrokerConnectivity({
    brokerId: userType === `distributor` && viewer ? viewer : undefined,
  })

  const [res] = useSearchPolicies({
    brokerId: viewer && userType === `distributor` ? viewer : undefined,
    claimsAdminId: viewer && userType === `claims_admin` ? viewer : undefined,
    carrierId: viewer && userType === `carrier` ? viewer : undefined,
    status: status ?? undefined,
    search: debouncedSearch,
    limit,
  })

  const collection = res.result?.tag !== `ok` ? emptyCollection<Policy>() : res.result.data
  const brokerConnectivity = resConnectivity.result?.tag !== `ok` ? null : resConnectivity.result.data
  const loading = res.loading || res.result === undefined

  return (
    <>
      <DocumentTitleOverride>Flock Cover | Policies</DocumentTitleOverride>
      <ListWrapper<Maybe<Status>>
        aboveTable={
          resConnectivity?.loading || brokerConnectivity ? (
            <OverallConnectivity
              loading={resConnectivity.loading}
              connectivityRate={brokerConnectivity ?? null}
              dataTestId={POLICIES_DTI.CONNECTIVITY_BAR_DTI}
            />
          ) : null
        }
        title="Policies"
        subTitle="Your customer's policies"
        dataCount={collection.total}
        listApiStatus={{
          loading,
          failed: res.result?.tag === `err`,
        }}
        filter={{
          defaultDisplayValue: `All`,
          value: status,
          action: (value: Maybe<Status>) => setStatus(value),
          items: [
            {
              text: `Active`,
              value: `Active`,
            },
            {
              text: `Cancelled`,
              value: `Cancelled`,
            },
            {
              text: `Void`,
              value: `Void`,
            },
            {
              text: `All`,
              value: null,
            },
          ],
        }}
        search={{
          value: search,
          onChange: setSearch,
          debounceDelay: 700,
        }}
      >
        <PoliciesTable onLimitChange={setLimit} collection={collection} loading={loading} limit={limit} />
      </ListWrapper>
    </>
  )
}
